/**
 * SponsorManagement - CRUD for event sponsors
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Building2, ExternalLink, Loader2 } from 'lucide-react';
import { sponsorAPI } from '@/lib/api';
import { MediaUpload } from '@/components/MediaUpload';

const TIERS = ['platinum', 'gold', 'silver', 'bronze', 'partner'];
const TIER_COLORS = {
  platinum: 'bg-slate-200 text-slate-800',
  gold: 'bg-yellow-100 text-yellow-800',
  silver: 'bg-gray-200 text-gray-800',
  bronze: 'bg-orange-100 text-orange-800',
  partner: 'bg-blue-100 text-blue-800'
};

// Form component extracted outside to prevent re-renders
const SponsorForm = ({ sponsor, onSubmit, onCancel }) => {
  const [form, setForm] = useState(sponsor || { 
    name: '', 
    tier: 'silver', 
    website_url: '', 
    description: '', 
    contact_name: '', 
    email: '', 
    logo_url: '',
    sponsorship_amount: '',
    is_featured: false
  });
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Company Name" /></div>
        <div><Label>Tier</Label>
          <Select value={form.tier} onValueChange={v => setForm({...form, tier: v})}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{TIERS.map(t => <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Website</Label><Input value={form.website_url || ''} onChange={e => setForm({...form, website_url: e.target.value})} placeholder="https://..." /></div>
        <MediaUpload value={form.logo_url || ''} onChange={(url) => setForm({ ...form, logo_url: url })} purpose="logo" label="Logo" testId="sponsor-logo-upload" previewClassName="h-16" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Sponsorship Amount</Label><Input type="number" value={form.sponsorship_amount || ''} onChange={e => setForm({...form, sponsorship_amount: e.target.value})} placeholder="50000" /></div>
        <div className="flex items-center gap-2 pt-6">
          <Switch checked={form.is_featured} onCheckedChange={v => setForm({...form, is_featured: v})} />
          <Label>Featured Sponsor</Label>
        </div>
      </div>
      <div><Label>Description</Label><Textarea value={form.description || ''} onChange={e => setForm({...form, description: e.target.value})} rows={2} /></div>
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Contact Name</Label><Input value={form.contact_name || ''} onChange={e => setForm({...form, contact_name: e.target.value})} /></div>
        <div><Label>Contact Email</Label><Input value={form.email || ''} onChange={e => setForm({...form, email: e.target.value})} type="email" /></div>
      </div>
      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={() => onSubmit(form)} disabled={!form.name}>{sponsor ? 'Update' : 'Create'} Sponsor</Button>
      </div>
    </div>
  );
};

const SponsorManagement = ({ eventId }) => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState(null);
  const [filterTier, setFilterTier] = useState('all');

  // Helper function to extract error message
  const getErrorMessage = (err, defaultMsg) => {
    const data = err.response?.data;
    if (typeof data === 'string') return data;
    if (data?.detail) return typeof data.detail === 'string' ? data.detail : defaultMsg;
    if (Array.isArray(data) && data[0]?.msg) return data[0].msg;
    return defaultMsg;
  };

  const { data: sponsors = [], isLoading } = useQuery({
    queryKey: ['sponsors', eventId, filterTier],
    queryFn: () => sponsorAPI.adminList(eventId).then(r => {
      const data = Array.isArray(r.data) ? r.data : [];
      if (filterTier === 'all') return data;
      return data.filter(s => s.tier === filterTier);
    }),
  });

  const createMutation = useMutation({
    mutationFn: (data) => sponsorAPI.create(eventId, data),
    onSuccess: () => {
      toast.success('Sponsor created');
      queryClient.invalidateQueries(['sponsors', eventId]);
      setIsOpen(false);
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to create sponsor'))
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => sponsorAPI.update(eventId, id, data),
    onSuccess: () => {
      toast.success('Sponsor updated');
      queryClient.invalidateQueries(['sponsors', eventId]);
      setEditingSponsor(null);
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to update'))
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => sponsorAPI.delete(eventId, id),
    onSuccess: () => {
      toast.success('Sponsor deleted');
      queryClient.invalidateQueries(['sponsors', eventId]);
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to delete'))
  });

  if (isLoading) return <div className="flex justify-center p-8"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="sponsor-management">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h2 className="text-2xl font-bold flex items-center gap-2"><Building2 className="h-6 w-6" />Sponsors</h2>
          <Select value={filterTier} onValueChange={setFilterTier}>
            <SelectTrigger className="w-32"><SelectValue placeholder="All Tiers" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tiers</SelectItem>
              {TIERS.map(t => <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild><Button><Plus className="h-4 w-4 mr-2" />Add Sponsor</Button></DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Add Sponsor</DialogTitle></DialogHeader>
            <SponsorForm onSubmit={data => createMutation.mutate(data)} onCancel={() => setIsOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sponsors.map(sponsor => (
          <Card key={sponsor.id} data-testid={`sponsor-${sponsor.id}`}>
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  {sponsor.logo_url ? <img src={sponsor.logo_url} alt="" className="h-10 w-10 object-contain rounded" /> : <Building2 className="h-10 w-10 text-slate-400" />}
                  <div>
                    <CardTitle className="text-lg">{sponsor.name}</CardTitle>
                    <Badge className={TIER_COLORS[sponsor.tier]}>{sponsor.tier}</Badge>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setEditingSponsor(sponsor)}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteMutation.mutate(sponsor.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="text-sm text-slate-600">
              {sponsor.description && <p className="line-clamp-2 mb-2">{sponsor.description}</p>}
              {(sponsor.website_url || sponsor.website) && <a href={sponsor.website_url || sponsor.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 flex items-center gap-1"><ExternalLink className="h-3 w-3" />Website</a>}
              {(sponsor.email || sponsor.contact_email) && <p className="text-xs mt-1">Contact: {sponsor.contact_name} ({sponsor.email || sponsor.contact_email})</p>}
            </CardContent>
          </Card>
        ))}
        {sponsors.length === 0 && <p className="col-span-3 text-center text-slate-500 py-8">No sponsors yet. Add your first sponsor!</p>}
      </div>

      <Dialog open={!!editingSponsor} onOpenChange={() => setEditingSponsor(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Sponsor</DialogTitle></DialogHeader>
          {editingSponsor && <SponsorForm sponsor={editingSponsor} onSubmit={data => updateMutation.mutate({ id: editingSponsor.id, data })} onCancel={() => setEditingSponsor(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SponsorManagement;
